import {
  custodianSelector,
  custodiansWithSameParentIdSelector,
  isCustodianEmpty,
  getNonEmptyCustodiansInSection,
  portfolioSelector,
  sheetSelector,
  sectionSelector,
  sheetSectionsSelector,
  getTickerUsingId,
  getTickerUsingShortName,
  defaultCustodianTickerSelector,
  isFiatTicker,
  getExchangeRate,
  getExchangeRateDetails,
  getSortKeyBetween,
  getUuid,
  isSheetEmpty,
  PVST_VALUE_TICKER_ID,
  tickerTypes,
  costType,
  irrTypes,
  updateCustodianInBulkAction,
  bulkSyncCustodianEntities,
  insertCustodianAtEndOfSection,
  insertSection,
  updateSection,
  updateDashboardAction,
  updateAiImportDocumentUsageStatus,
  getDateInDDMMMYYYY,
  formatAMPM,
  accountLinkingService,
  accountLinkingContainers,
  getAccountLinkingService,
  getAggregatorName,
  capitalizeStringWithSpaces,
  parseLastLink,
  isAiSyncLastLink,
  getParsedLastLinkNames,
  getLastLinkNames,
  buildCustodianOrigin,
  userSelector,
  toUserTimezoneDate
} from "@kubera/common";
import { gridCellPadding } from "components/grid/GridCellExports";
import i18n from "i18next";
import { category } from "components/dashboard/DashboardComponentExports";

export const aiImportContext = {
  ALL: "all",
  CASHFLOW: "cashFlow",
  FUND_SCHEDULE: "fundSchedule",
  ACCOUNT: "account",
  EXTENSION: "extension"
};

export const aiImportSubContext = {
  MANUAL: "manual",
  STOCKS: "stocks",
  CRYPTO: "crypto",
  ALL: "all"
};

export const itemState = {
  UPDATE: "UPDATE",
  ARCHIVE: "ARCHIVE",
  SAME: "SAME",
  NEW: "NEW",
  IGNORED: "IGNORED",
  UNKNOWN: ""
};

export const hasIgnoreStateChange = (rows, findExtractedData) =>
  rows.some(row => {
    const extractedData = findExtractedData(row.id);
    if (!extractedData) return false;
    return (extractedData.ignore === true) !== (row.cells[0].itemState === itemState.IGNORED);
  });

export const parentTargetType = {
  EXISTING_PARENT: "existingParent",
  NEW_PARENT: "newParent",
  NO_PARENT: "noParent"
};

export const itemStateBadgeCellWidth = "35px";

export const itemStateBadgeMarginRight = `calc(-1 * (${gridCellPadding.DEFAULT} + 1px))`;

export const getBadgeColorForItemState = (state, isDisabled = false) => {
  if (isDisabled === true) {
    return "#D2D2D2";
  }
  if (state === itemState.NEW) {
    return "#FF0202";
  }
  if (state === itemState.UPDATE) {
    return "#AFF2D8";
  }
  if (state === itemState.ARCHIVE) {
    return "#BE5F00";
  }
  if (state === itemState.SAME) {
    return "#DCDCDC";
  }
  if (state === itemState.IGNORED) {
    return "#EBEBEB";
  }
  if (state === itemState.UNKNOWN) {
    return "#FFFFFF";
  }
};

export const getDefaultSectionName = (state, isAiSync = false) => {
  const prefix = isAiSync ? i18n.t("aiSync") : i18n.t("aiImport");
  return `${prefix} ${getDateInDDMMMYYYY(toUserTimezoneDate(state))} at ${formatAMPM(state, new Date())}`;
};

export const findReusableEmptySection = (state, portfolioId, sheetId) =>
  sheetSectionsSelector(state, portfolioId, sheetId).find(
    section => getNonEmptyCustodiansInSection(state, portfolioId, section.id).length === 0
  ) || null;

export const buildTargetSectionLocation = (state, portfolioId, sheet, name) => {
  const reusable = findReusableEmptySection(state, portfolioId, sheet.id);
  if (reusable) {
    const section = { ...reusable, name };
    return { isSection: true, createNewSection: true, reuseEmptySection: true, section, sheet, ...section };
  }
  const isEmptySheet = isSheetEmpty(state, portfolioId, sheet.id);
  const sections = sheetSectionsSelector(state, portfolioId, sheet.id);
  const section = {
    id: getUuid(),
    name,
    sheetId: sheet.id,
    sortKey: isEmptySheet
      ? getSortKeyBetween(null, sections[0]?.sortKey)
      : getSortKeyBetween(sections[sections.length - 1]?.sortKey, null)
  };
  return { isSection: true, createNewSection: true, reuseEmptySection: false, section, sheet, ...section };
};

// A parent account is created into a section, so any target the user picked has to be reduced to a
// section-shaped location before it can carry a newParentCustodianId.
export const buildParentSectionLocation = (state, portfolioId, location) => {
  if (!location) {
    return null;
  }
  const section =
    location.section ||
    (location.isSection === true ? location : null) ||
    (location.isSheet === true ? sheetSectionsSelector(state, portfolioId, location.id)[0] : null);
  if (!section) {
    return null;
  }
  const sheet = location.sheet || sheetSelector(state, section.sheetId);
  if (!sheet) {
    return null;
  }
  return {
    isSection: true,
    createNewSection: location.createNewSection === true,
    reuseEmptySection: location.reuseEmptySection === true,
    section,
    sheet,
    ...section
  };
};

export const findExtractedAccountById = (account, accountId) => {
  if (!account || !accountId) {
    return null;
  }
  return (
    (account.asset || []).find(item => item.id === accountId) ||
    (account.debt || []).find(item => item.id === accountId) ||
    null
  );
};

const isSingleSectionTargetSheet = (state, portfolioId, location) =>
  (location.createNewSection !== true || location.reuseEmptySection === true) &&
  sheetSectionsSelector(state, portfolioId, location.sheet.id).length === 1;

export const getLocationPath = (state, portfolioId, location, defaultMessage = "", showParentName = true) => {
  if (!location) {
    return defaultMessage;
  }
  if (location.isNewAccount === true && location.sheet && location.section) {
    const isSingleSectionSheet = isSingleSectionTargetSheet(state, portfolioId, location);
    const basePath = isSingleSectionSheet ? location.sheet.name : `${location.sheet.name} / ${location.section.name}`;
    if (showParentName && location.isParent === true && location.parentCustodianName) {
      const parentLabel =
        location.isNewParent === true
          ? `${location.parentCustodianName} ${i18n.t("aiImport.newAccountSuffix")}`
          : location.parentCustodianName;
      return `${basePath} / ${parentLabel}`;
    }
    return location.createNewSection === true ? `${basePath} ${i18n.t("aiImport.newSectionSuffix")}` : basePath;
  }
  if (location.isCustodian || (location.isParent === true && location.sheet && location.section)) {
    const isSingleSectionSheet = isSingleSectionTargetSheet(state, portfolioId, location);
    const basePath = isSingleSectionSheet ? location.sheet.name : `${location.sheet.name} / ${location.section.name}`;
    if (showParentName) {
      const parentName =
        location.isParent === true
          ? location.parentCustodianName
          : location.parentId
          ? custodianSelector(state, location.parentId)?.name
          : null;
      if (parentName) {
        const parentLabel =
          location.isParent === true && location.isNewParent === true
            ? `${parentName} ${i18n.t("aiImport.newAccountSuffix")}`
            : parentName;
        return `${basePath} / ${parentLabel}`;
      }
    }
    return basePath;
  } else if (location.isSection) {
    const isSingleSectionSheet = isSingleSectionTargetSheet(state, portfolioId, location);
    const basePath = isSingleSectionSheet ? location.sheet.name : `${location.sheet.name} / ${location.name}`;
    return location.createNewSection === true ? `${basePath} ${i18n.t("aiImport.newSectionSuffix")}` : basePath;
  } else if (location.isSheet) {
    return `${location.name}`;
  }
  return null;
};

export const stripPageUrlForLastLink = pageUrl => {
  if (!pageUrl) return "";
  try {
    const hasScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(pageUrl);
    const hostname = new URL(hasScheme ? pageUrl : `https://${pageUrl}`).hostname;
    return hostname.replace(/^www\./, "");
  } catch (e) {
    return "";
  }
};

export const buildLastLinkMetadata = (dataExtracted, pageUrl, source) => ({
  institution: dataExtracted?.institution,
  pageUrl: stripPageUrlForLastLink(pageUrl),
  loggedInUserEmail: dataExtracted?.loggedInUserEmail,
  loggedInUserId: dataExtracted?.loggedInUserId,
  loggedInUserName: dataExtracted?.loggedInUserName,
  source,
  ts: Math.floor(Date.now() / 1000)
});

const LAST_LINK_KEY_ORDER = ["name", "institution", "url", "email", "userId", "userName", "source", "ts", "old"];

const stringifyLastLink = obj => {
  const ordered = {};
  for (const key of LAST_LINK_KEY_ORDER) {
    const value = obj[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (key === "name") {
      if (Array.isArray(value) && value.length > 0) {
        ordered[key] = value;
      }
      continue;
    }
    if (typeof value === "string" && value.trim().length === 0) {
      continue;
    }
    ordered[key] = value;
  }
  return Object.keys(ordered).length === 0 ? "" : JSON.stringify(ordered);
};

export const buildCustodianLastLink = (existingLastLink, lastLinkMetadata, itemName) => {
  const existing = existingLastLink || "";
  const parsed = parseLastLink(existing);
  const obj = parsed ? { ...parsed } : {};
  if (!parsed && existing.trim().length > 0 && obj.old === undefined) {
    obj.old = existing;
  }

  const names = [...getParsedLastLinkNames(obj)];
  if (typeof itemName === "string" && itemName.trim().length > 0 && !names.includes(itemName)) {
    names.push(itemName);
  }
  if (names.length > 0) {
    obj.name = names;
  }

  const assign = (key, value) => {
    if (typeof value === "string" && value.trim().length > 0) {
      obj[key] = value;
    }
  };
  assign("institution", lastLinkMetadata?.institution);
  assign("url", lastLinkMetadata?.pageUrl);
  assign("email", lastLinkMetadata?.loggedInUserEmail);
  assign("userId", lastLinkMetadata?.loggedInUserId);
  assign("userName", lastLinkMetadata?.loggedInUserName);
  assign("source", lastLinkMetadata?.source);
  if (typeof lastLinkMetadata?.ts === "number") {
    obj.ts = lastLinkMetadata.ts;
  }

  return stringifyLastLink(obj);
};

export const buildCashflowLastLink = name => {
  if (typeof name !== "string" || name.trim().length === 0) {
    return "";
  }
  return stringifyLastLink({ name: [name] });
};

const normalizeCustodianName = name =>
  typeof name === "string"
    ? name
        .trim()
        .replace(/\s+/g, " ")
        .toLowerCase()
    : "";

const getParentCustodianNameKeys = (parentAccount, institution) => {
  const nameKey = normalizeCustodianName(parentAccount);
  if (nameKey.length === 0) {
    return [];
  }
  const institutionKey = normalizeCustodianName(institution);
  if (institutionKey.length === 0 || nameKey.includes(institutionKey)) {
    return [nameKey];
  }
  return [nameKey, `${institutionKey} - ${nameKey}`];
};

const hasMatchingInstitution = (parsedLastLink, institutionKey) => {
  const parsedInstitutionKey = normalizeCustodianName(parsedLastLink?.institution);
  return institutionKey.length === 0 || parsedInstitutionKey.length === 0 || parsedInstitutionKey === institutionKey;
};

const addParentCandidateToIndex = (index, nameKey, custodian) => {
  const matches = index.get(nameKey);
  if (matches) {
    matches.push(custodian);
  } else {
    index.set(nameKey, [custodian]);
  }
};

const buildAiSyncParentCandidates = (state, portfolioId, nameKeys, institution, excludedCustodianIds) => {
  const byName = new Map();
  const renamed = [];
  if (nameKeys.size === 0) {
    return { byName, renamed };
  }
  const institutionKey = normalizeCustodianName(institution);
  const custodians = portfolioSelector(state, portfolioId)?.details?.custodian || [];
  for (const custodian of custodians) {
    if (custodian.parentId || custodian.tsArchive || excludedCustodianIds.has(custodian.id)) {
      continue;
    }
    if (custodian.linkType && custodian.linkType !== accountLinkingService.MANUAL_CONNECTED) {
      continue;
    }
    if (!isAiSyncLastLink(custodian.lastLink)) {
      continue;
    }
    const nameKey = normalizeCustodianName(custodian.name);
    if (!nameKeys.has(nameKey)) {
      renamed.push(custodian);
      continue;
    }
    if (!hasMatchingInstitution(parseLastLink(custodian.lastLink), institutionKey)) {
      continue;
    }
    addParentCandidateToIndex(byName, nameKey, custodian);
  }
  return { byName, renamed };
};

const buildAiSyncParentLastLinkNameIndex = (candidates, nameKeys, institution) => {
  const index = new Map();
  const institutionKey = normalizeCustodianName(institution);
  for (const custodian of candidates) {
    const parsed = parseLastLink(custodian.lastLink);
    if (!hasMatchingInstitution(parsed, institutionKey)) {
      continue;
    }
    for (const name of getParsedLastLinkNames(parsed)) {
      const nameKey = normalizeCustodianName(name);
      if (nameKeys.has(nameKey)) {
        addParentCandidateToIndex(index, nameKey, custodian);
      }
    }
  }
  return index;
};

const findExistingAiSyncParentTarget = (state, index, nameKeys, isAsset) => {
  const sheetCategory = isAsset ? category.ASSET : category.DEBT;
  for (const nameKey of nameKeys) {
    for (const custodian of index.get(nameKey) || []) {
      const section = sectionSelector(state, custodian.sectionId);
      const sheet = section ? sheetSelector(state, section.sheetId) : null;
      if (!section || !sheet || sheet.category !== sheetCategory) {
        continue;
      }
      return {
        type: parentTargetType.EXISTING_PARENT,
        parentCustodianId: custodian.id,
        displayName: custodian.name,
        section,
        sheet
      };
    }
  }
  return null;
};

const isFiatTickerId = (state, tickerId) => isFiatTicker(getTickerUsingId(state, tickerId));

const isFiatLocation = (state, location) => isFiatTickerId(state, location.valueTickerId);

const getSelectableParentHoldings = (state, portfolioId, parentCustodianId, sectionId, excludedCustodianIds) =>
  custodiansWithSameParentIdSelector(state, portfolioId, parentCustodianId).filter(
    holding => !holding.tsArchive && holding.sectionId === sectionId && !excludedCustodianIds.has(holding.id)
  );

const buildParentHoldingIndex = (state, holdings) => {
  const byName = new Map();
  for (const holding of holdings) {
    const nameKey = normalizeCustodianName(holding.name);
    if (!byName.has(nameKey)) {
      byName.set(nameKey, holding);
    }
  }
  let byLastLinkName = null;
  const getByLastLinkName = () => {
    if (!byLastLinkName) {
      byLastLinkName = new Map();
      for (const holding of holdings) {
        for (const name of getLastLinkNames(holding.lastLink)) {
          const nameKey = normalizeCustodianName(name);
          if (!byLastLinkName.has(nameKey)) {
            byLastLinkName.set(nameKey, holding);
          }
        }
      }
    }
    return byLastLinkName;
  };
  let byTicker = null;
  const getByTicker = () => {
    if (!byTicker) {
      byTicker = new Map();
      for (const holding of holdings) {
        const tickerId = holding.valueTickerId;
        if (
          tickerId === undefined ||
          tickerId === null ||
          tickerId === PVST_VALUE_TICKER_ID ||
          isFiatTickerId(state, tickerId)
        ) {
          continue;
        }
        if (!byTicker.has(tickerId)) {
          byTicker.set(tickerId, holding);
        }
      }
    }
    return byTicker;
  };
  return { byName, getByLastLinkName, getByTicker };
};

const findParentHoldingForName = (index, name, claimedHoldingIds) => {
  const nameKey = normalizeCustodianName(name);
  if (nameKey.length === 0) {
    return null;
  }
  const holding = index.byName.get(nameKey) || index.getByLastLinkName().get(nameKey);
  if (!holding || claimedHoldingIds.has(holding.id)) {
    return null;
  }
  return holding;
};

const findParentHoldingForTicker = (index, tickerId, claimedHoldingIds) => {
  if (tickerId === undefined || tickerId === null) {
    return null;
  }
  const holding = index.getByTicker().get(tickerId);
  if (!holding || claimedHoldingIds.has(holding.id)) {
    return null;
  }
  return holding;
};

const matchChildItemsToParentHoldings = (state, portfolioId, target, children, matchedCustodianIds) => {
  const unmatchedChildren = children.filter(child => !child.matchedCustodianId);
  if (unmatchedChildren.length === 0) {
    return;
  }
  const holdings = getSelectableParentHoldings(
    state,
    portfolioId,
    target.parentCustodianId,
    target.section.id,
    matchedCustodianIds
  );
  if (holdings.length === 0) {
    return;
  }
  const index = buildParentHoldingIndex(state, holdings);
  const claimedHoldingIds = new Set();
  for (const child of unmatchedChildren) {
    const holding = findParentHoldingForName(index, child.name, claimedHoldingIds);
    if (!holding) {
      continue;
    }
    claimedHoldingIds.add(holding.id);
    child.matchedCustodianId = holding.id;
  }
};

// A holding targeted at an existing account updates that account's matching entry instead of adding a
// duplicate one. The ticker fallback deliberately runs only here and not in the scan: picking an
// account is an explicit user choice, which justifies a looser match than an automatic one.
export const resolveExistingHoldingLocation = (
  state,
  portfolioId,
  parentLocation,
  itemName,
  tickerId,
  claimedCustodianIds = new Set()
) => {
  if (
    parentLocation?.isParent !== true ||
    parentLocation.isNewParent === true ||
    !parentLocation.parentCustodianId ||
    !parentLocation.section?.id
  ) {
    return null;
  }
  const holdings = getSelectableParentHoldings(
    state,
    portfolioId,
    parentLocation.parentCustodianId,
    parentLocation.section.id,
    claimedCustodianIds
  );
  if (holdings.length === 0) {
    return null;
  }
  const index = buildParentHoldingIndex(state, holdings);
  const holding =
    findParentHoldingForName(index, itemName, new Set()) || findParentHoldingForTicker(index, tickerId, new Set());
  if (!holding) {
    return null;
  }
  const section = sectionSelector(state, holding.sectionId);
  const sheet = section ? sheetSelector(state, section.sheetId) : null;
  if (!section || !sheet) {
    return null;
  }
  return { isCustodian: true, section, sheet, ...holding, chosenParentLocation: parentLocation };
};

export const buildAccountParentGroups = (state, dataExtracted, currentPortfolio, institution) => {
  const assets = dataExtracted.asset || [];
  const debts = dataExtracted.debt || [];

  const groupMap = new Map();
  const collect = (items, isAsset) => {
    for (const item of items) {
      if (!item.parentAccount) continue;
      const key = `${isAsset ? "asset" : "debt"}::${item.parentAccount}`;
      if (!groupMap.has(key)) {
        groupMap.set(key, { key, parentAccount: item.parentAccount, isAsset, children: [] });
      }
      groupMap.get(key).children.push(item);
    }
  };
  collect(assets, true);
  collect(debts, false);

  const groups = [];
  const syntheticParentIds = new Set();
  const newParentIds = new Set();
  const newParentCustodianIdByParentId = new Map();
  const hiddenItemIds = new Set();
  const itemToGroupKey = new Map();
  const childTargetMap = new Map();
  const standaloneItemIds = new Set();

  const nameKeysByGroupKey = new Map();
  const allParentNameKeys = new Set();
  for (const group of groupMap.values()) {
    const nameKeys = getParentCustodianNameKeys(group.parentAccount, institution);
    nameKeysByGroupKey.set(group.key, nameKeys);
    for (const nameKey of nameKeys) {
      allParentNameKeys.add(nameKey);
    }
  }
  const matchedCustodianIds = new Set();
  for (const item of [...assets, ...debts]) {
    if (item.matchedCustodianId) {
      matchedCustodianIds.add(item.matchedCustodianId);
    }
  }
  let aiSyncParentCandidates = null;
  const getAiSyncParentCandidates = () => {
    if (!aiSyncParentCandidates) {
      aiSyncParentCandidates = buildAiSyncParentCandidates(
        state,
        currentPortfolio.id,
        allParentNameKeys,
        institution,
        matchedCustodianIds
      );
    }
    return aiSyncParentCandidates;
  };
  let aiSyncParentLastLinkNameIndex = null;
  const getAiSyncParentLastLinkNameIndex = () => {
    if (!aiSyncParentLastLinkNameIndex) {
      aiSyncParentLastLinkNameIndex = buildAiSyncParentLastLinkNameIndex(
        getAiSyncParentCandidates().renamed,
        allParentNameKeys,
        institution
      );
    }
    return aiSyncParentLastLinkNameIndex;
  };

  for (const { key, parentAccount, isAsset, children } of groupMap.values()) {
    const sameCategoryItems = isAsset ? assets : debts;
    let parentItem = sameCategoryItems.find(i => !i.parentAccount && i.name === parentAccount) || null;

    const matchedChild = children.find(c => c.matchedCustodianId);
    let target = null;

    if (matchedChild) {
      const matchedChildCustodian = custodianSelector(state, matchedChild.matchedCustodianId);
      const existingParentCustodian = matchedChildCustodian?.parentId
        ? custodianSelector(state, matchedChildCustodian.parentId)
        : null;
      if (existingParentCustodian && existingParentCustodian.holdingsCount > 0) {
        const section = sectionSelector(state, existingParentCustodian.sectionId);
        const sheet = section ? sheetSelector(state, section.sheetId) : null;
        if (section && sheet) {
          target = {
            type: parentTargetType.EXISTING_PARENT,
            parentCustodianId: existingParentCustodian.id,
            displayName: existingParentCustodian.name,
            section,
            sheet
          };
        }
      }
      if (!target) {
        const section = matchedChildCustodian ? sectionSelector(state, matchedChildCustodian.sectionId) : null;
        const sheet = section ? sheetSelector(state, section.sheetId) : null;
        if (section && sheet) {
          target = { type: parentTargetType.NO_PARENT, section, sheet };
        }
      }
    } else if (parentItem && parentItem.matchedCustodianId) {
      const existingParentCustodian = custodianSelector(state, parentItem.matchedCustodianId);
      const section = existingParentCustodian ? sectionSelector(state, existingParentCustodian.sectionId) : null;
      const sheet = section ? sheetSelector(state, section.sheetId) : null;
      if (existingParentCustodian && section && sheet) {
        target = {
          type: parentTargetType.EXISTING_PARENT,
          parentCustodianId: existingParentCustodian.id,
          displayName: existingParentCustodian.name,
          section,
          sheet
        };
      }
    }

    const parentNameKeys = nameKeysByGroupKey.get(key) || [];
    if (!target && !matchedChild && !parentItem && parentNameKeys.length > 0) {
      target =
        findExistingAiSyncParentTarget(state, getAiSyncParentCandidates().byName, parentNameKeys, isAsset) ||
        findExistingAiSyncParentTarget(state, getAiSyncParentLastLinkNameIndex(), parentNameKeys, isAsset);
      if (target) {
        matchChildItemsToParentHoldings(state, currentPortfolio.id, target, children, matchedCustodianIds);
      }
    }

    if (!target) {
      const defaultTicker = defaultCustodianTickerSelector(state, currentPortfolio.currency);
      const sumValue = children.reduce((sum, c) => {
        const details = isAsset
          ? getAssetDetails(state, c, defaultTicker.shortName)
          : getDebtDetails(state, c, defaultTicker.shortName);
        if (!details || details.value === undefined || details.value === null) return sum;
        const rate = getExchangeRate(state, details.currency, defaultTicker.shortName) || 1;
        return sum + Number(details.value) * rate;
      }, 0);
      let useParent = parentItem;
      if (!useParent) {
        useParent = {
          id: getUuid(),
          name: parentAccount,
          value: sumValue,
          valueTickerId: defaultTicker.id,
          isSyntheticParent: true
        };
        if (isAsset) assets.push(useParent);
        else debts.push(useParent);
        parentItem = useParent;
        syntheticParentIds.add(useParent.id);
      } else if (useParent.value === undefined && useParent.valueTickerId === undefined) {
        useParent.value = sumValue;
        useParent.valueTickerId = defaultTicker.id;
      }
      const newParentCustodianId = getUuid();
      newParentIds.add(useParent.id);
      newParentCustodianIdByParentId.set(useParent.id, newParentCustodianId);
      hiddenItemIds.add(useParent.id);
      target = {
        type: parentTargetType.NEW_PARENT,
        parentCustodianId: newParentCustodianId,
        displayName: useParent.name
      };
    }

    const isParentRendered = !!(parentItem && parentItem.matchedCustodianId);
    groups.push({ key, parentAccount, isAsset, parentItem, childItems: children, target, isParentRendered });
    if (parentItem) itemToGroupKey.set(parentItem.id, key);
    for (const c of children) {
      itemToGroupKey.set(c.id, key);
      childTargetMap.set(c.id, target);
    }
  }

  for (const item of [...assets, ...debts]) {
    if (!itemToGroupKey.has(item.id)) standaloneItemIds.add(item.id);
  }

  return {
    groups,
    syntheticParentIds,
    newParentIds,
    newParentCustodianIdByParentId,
    hiddenItemIds,
    itemToGroupKey,
    childTargetMap,
    standaloneItemIds
  };
};

const locationFromParentTarget = target => {
  if (target.type === parentTargetType.NO_PARENT) {
    return {
      isSection: true,
      section: target.section,
      sheet: target.sheet,
      ...target.section
    };
  }
  return {
    isParent: true,
    isNewParent: target.type === parentTargetType.NEW_PARENT,
    createNewSection: target.createNewSection === true,
    reuseEmptySection: target.reuseEmptySection === true,
    parentCustodianId: target.parentCustodianId,
    parentCustodianName: target.displayName,
    section: target.section,
    sheet: target.sheet
  };
};

export const buildAccountLocationMap = (
  state,
  dataExtracted,
  currentPortfolio,
  defaultTargetSheet,
  institution,
  isAiSync = false,
  presetNewCustodianIdByAccountId = {}
) => {
  const grouping = buildAccountParentGroups(state, dataExtracted, currentPortfolio, institution);

  const locationMap = {};
  const ignoredItemIds = [];
  let defaultAssetLocation = null;
  let defaultDebtLocation = null;

  const setLocation = (itemCategory, item) => {
    let matchedCustodian = item.matchedCustodianId ? custodianSelector(state, item.matchedCustodianId) : null;

    if (defaultTargetSheet && isSheetEmpty(state, currentPortfolio.id, defaultTargetSheet.id)) {
      matchedCustodian = null;
    }

    if (!matchedCustodian === false) {
      const custodian = custodianSelector(state, item.matchedCustodianId);
      const section = sectionSelector(state, custodian?.sectionId);
      const sheet = sheetSelector(state, section?.sheetId);
      if (!custodian || !section || !sheet) {
        return;
      }
      locationMap[item.id] = {
        isCustodian: true,
        section: section,
        sheet: sheet,
        ...custodian
      };
    } else if (itemCategory === category.ASSET && !defaultAssetLocation === false) {
      locationMap[item.id] = defaultAssetLocation;
    } else if (itemCategory === category.DEBT && !defaultDebtLocation === false) {
      locationMap[item.id] = defaultDebtLocation;
    } else {
      const sheet =
        defaultTargetSheet?.category === itemCategory
          ? defaultTargetSheet
          : currentPortfolio.details.sheet.filter(s => s.category === itemCategory)[0];

      const defaultLocation = buildTargetSectionLocation(
        state,
        currentPortfolio.id,
        sheet,
        institution || getDefaultSectionName(state, isAiSync)
      );

      if (itemCategory === category.ASSET) {
        defaultAssetLocation = defaultLocation;
        locationMap[item.id] = defaultAssetLocation;
      } else {
        defaultDebtLocation = defaultLocation;
        locationMap[item.id] = defaultDebtLocation;
      }
    }
  };

  const assets = dataExtracted.asset || [];
  const debts = dataExtracted.debt || [];

  for (const asset of assets) {
    if (asset.tickerId === PVST_VALUE_TICKER_ID) {
      asset.tickerId = undefined;
    }
    if (asset.quantity && asset.tickerId && getTickerUsingId(state, asset.tickerId).type === tickerTypes.FIAT) {
      if (!asset.value === true) {
        asset.value = asset.quantity;
        asset.valueTickerId = asset.tickerId;
      }
      asset.quantity = undefined;
    }
    if (asset.ignore === true) {
      ignoredItemIds.push(asset.id);
    }
    setLocation(category.ASSET, asset);
  }

  for (const debt of debts) {
    if (debt.tickerId === PVST_VALUE_TICKER_ID) {
      debt.tickerId = undefined;
    }
    if (debt.ignore === true) {
      ignoredItemIds.push(debt.id);
    }
    setLocation(category.DEBT, debt);
  }

  for (const parentItemId of grouping.newParentIds) {
    const parentLocation = locationMap[parentItemId];
    if (!parentLocation) continue;
    locationMap[parentItemId] = {
      ...parentLocation,
      newParentCustodianId: grouping.newParentCustodianIdByParentId.get(parentItemId)
    };
  }

  for (const group of grouping.groups) {
    if (group.target.type === parentTargetType.NEW_PARENT && group.parentItem) {
      const parentLocation = locationMap[group.parentItem.id];
      if (parentLocation) {
        const section = parentLocation.section || (parentLocation.isSection ? parentLocation : null);
        group.target.section = section;
        group.target.sheet = parentLocation.sheet || (section ? sheetSelector(state, section.sheetId) : null);
        group.target.createNewSection = parentLocation.createNewSection === true;
        group.target.reuseEmptySection = parentLocation.reuseEmptySection === true;
      }
    }
    for (const child of group.childItems) {
      if (!child.matchedCustodianId) {
        locationMap[child.id] = locationFromParentTarget(group.target);
      }
    }
  }

  const newCustodianIdByAccountId = {};
  for (const item of [...assets, ...debts]) {
    const loc = locationMap[item.id];
    if (!loc || loc.isCustodian === true || loc.newParentCustodianId !== undefined || item.matchedCustodianId) {
      continue;
    }
    const newCustodianId = presetNewCustodianIdByAccountId[item.id] || getUuid();
    locationMap[item.id] = { ...loc, newCustodianId };
    newCustodianIdByAccountId[item.id] = newCustodianId;
  }

  return {
    locationMap,
    ignoredItemIds,
    normalizedAssets: assets,
    normalizedDebts: debts,
    grouping,
    hiddenItemIds: grouping.hiddenItemIds,
    syntheticParentIds: grouping.syntheticParentIds,
    newCustodianIdByAccountId
  };
};

const buildArchiveEntryValueFields = (state, custodian, isAsset) => {
  const fields = {};
  if (isAsset && custodian.cost !== undefined && custodian.cost !== null) {
    fields.cost = custodian.cost;
    fields.costTickerId = custodian.costTickerId;
  }
  if (custodian.valueTickerId === PVST_VALUE_TICKER_ID) {
    fields.quantity = custodian.value;
    try {
      const parsed = custodian.rate ? JSON.parse(custodian.rate) : null;
      if (parsed && parsed.t !== undefined && parsed.p !== undefined) {
        fields.price = parsed.p;
        fields.priceTickerId = parsed.t;
      }
    } catch (e) {}
    return fields;
  }
  const ticker = custodian.valueTickerId ? getTickerUsingId(state, custodian.valueTickerId) : null;
  if (ticker && ticker.type !== tickerTypes.FIAT) {
    fields.quantity = custodian.value;
    fields.tickerId = custodian.valueTickerId;
    return fields;
  }
  fields.value = custodian.value;
  fields.valueTickerId = custodian.valueTickerId;
  return fields;
};

export const isExtractedDataEmpty = data => {
  if (!data) return true;
  const counts = [
    data.account?.asset?.length,
    data.account?.debt?.length,
    data.cashFlow?.cashIn?.length,
    data.cashFlow?.cashOut?.length,
    data.capitalCall?.items?.length,
    data.distributionSchedule?.items?.length
  ];
  return counts.every(n => !n);
};

export const detectArchiveHoldings = (state, dataExtracted, portfolio, pageUrl) => {
  const account = dataExtracted?.account;
  if (!account || !portfolio) {
    return [];
  }
  if (!account.asset) account.asset = [];
  if (!account.debt) account.debt = [];
  const assets = account.asset;
  const debts = account.debt;

  const responseMatchedIds = new Set();
  for (const item of [...assets, ...debts]) {
    if (item.matchedCustodianId) {
      responseMatchedIds.add(item.matchedCustodianId);
    }
  }
  if (responseMatchedIds.size === 0) {
    return [];
  }

  const eligibleParentIds = new Set();
  for (const matchedId of responseMatchedIds) {
    const matchedChild = custodianSelector(state, matchedId);
    if (!matchedChild || !matchedChild.parentId) {
      continue;
    }
    const parent = custodianSelector(state, matchedChild.parentId);
    if (parent && parent.holdingsCount > 0) {
      eligibleParentIds.add(parent.id);
    }
  }
  if (eligibleParentIds.size === 0) {
    return [];
  }

  const strippedUrl = stripPageUrlForLastLink(pageUrl);
  if (typeof strippedUrl !== "string" || strippedUrl.trim().length === 0) {
    return [];
  }
  const identity = {
    url: strippedUrl,
    email: dataExtracted?.loggedInUserEmail,
    userId: dataExtracted?.loggedInUserId,
    userName: dataExtracted?.loggedInUserName
  };
  const identityPieces = [identity.email, identity.userId, identity.userName].filter(
    piece => typeof piece === "string" && piece.trim().length > 0
  );
  if (identityPieces.length === 0) {
    return [];
  }
  const hasPiece = (parsed, field, value) => {
    if (typeof value !== "string" || value.trim().length === 0) {
      return true;
    }
    return parsed[field] === value;
  };
  const lastLinkMatches = lastLink => {
    const parsed = parseLastLink(lastLink);
    if (!parsed) {
      return false;
    }
    return (
      hasPiece(parsed, "url", identity.url) &&
      hasPiece(parsed, "email", identity.email) &&
      hasPiece(parsed, "userId", identity.userId) &&
      hasPiece(parsed, "userName", identity.userName)
    );
  };

  const archiveItems = [];
  for (const parentId of eligibleParentIds) {
    const parent = custodianSelector(state, parentId);
    if (!parent || !lastLinkMatches(parent.lastLink)) {
      continue;
    }
    const children = custodiansWithSameParentIdSelector(state, portfolio.id, parentId);
    for (const child of children) {
      if (responseMatchedIds.has(child.id) || child.tsArchive || isCustodianEmpty(child)) {
        continue;
      }
      const section = sectionSelector(state, child.sectionId);
      const sheet = section ? sheetSelector(state, section.sheetId) : null;
      if (!section || !sheet) {
        continue;
      }
      const entry = {
        id: getUuid(),
        name: child.name,
        matchedCustodianId: child.id,
        parentAccount: parent.name,
        archive: true,
        ...buildArchiveEntryValueFields(state, child, sheet.category === category.ASSET)
      };
      if (sheet.category === category.DEBT) {
        debts.push(entry);
      } else {
        assets.push(entry);
      }
      archiveItems.push(entry);
    }
  }

  return archiveItems;
};

export const normalizeAssetForLocation = (state, asset, location) => {
  if (location?.isCustodian !== true || !location.valueTickerId || location.valueTickerId === PVST_VALUE_TICKER_ID) {
    return asset;
  }
  const next = { ...asset };
  const fiatLocation = isFiatLocation(state, location);
  if (fiatLocation && next.value === undefined && next.quantity !== undefined && next.price !== undefined) {
    next.value = next.quantity * next.price;
  }
  if (next.value !== undefined && (fiatLocation || next.quantity === undefined)) {
    next.valueTickerId = location.valueTickerId;
    next.quantity = undefined;
    next.price = undefined;
    next.priceTickerId = undefined;
    next.tickerId = undefined;
  } else if (next.quantity !== undefined) {
    next.tickerId = location.valueTickerId;
  }
  return next;
};

export const normalizeDebtForLocation = (debt, location) => {
  if (location?.isCustodian !== true || !location.valueTickerId || location.valueTickerId === PVST_VALUE_TICKER_ID) {
    return debt;
  }
  const next = { ...debt };
  if (next.quantity !== undefined) {
    next.tickerId = location.valueTickerId;
  } else if (next.value !== undefined) {
    next.valueTickerId = location.valueTickerId;
  }
  return next;
};

export const getAssetDetails = (state, asset, portfolioCurrency) => {
  const portfolioTicker = getTickerUsingShortName(state, portfolioCurrency);
  if (asset.quantity !== undefined && asset.tickerId) {
    return {
      value: asset.quantity,
      valueTickerId: asset.tickerId,
      currency: getTickerUsingId(state, asset.tickerId).shortName
    };
  } else if (asset.quantity !== undefined && !asset.tickerId && asset.price !== undefined && asset.priceTickerId) {
    return {
      value: asset.quantity,
      valueTickerId: PVST_VALUE_TICKER_ID,
      currency: getTickerUsingId(state, PVST_VALUE_TICKER_ID).shortName,
      valueExchangeRate: getExchangeRateDetails(
        state,
        portfolioTicker.id,
        getExchangeRate(state, getTickerUsingId(state, asset.priceTickerId).shortName, portfolioTicker.shortName)
      ),
      rate: JSON.stringify({ u: 1, t: asset.priceTickerId, p: asset.price })
    };
  } else if (asset.quantity !== undefined && !asset.tickerId && !asset.price && !asset.value) {
    return {
      value: asset.quantity,
      valueTickerId: PVST_VALUE_TICKER_ID,
      currency: getTickerUsingId(state, PVST_VALUE_TICKER_ID).shortName,
      valueExchangeRate: getExchangeRateDetails(state, portfolioTicker.id, 1),
      rate: JSON.stringify({ u: 1, t: portfolioTicker.id, p: 0 })
    };
  } else if (asset.value !== undefined && asset.valueTickerId) {
    return {
      value: asset.value,
      valueTickerId: asset.valueTickerId,
      currency: getTickerUsingId(state, asset.valueTickerId).shortName
    };
  }
};

export const getDebtDetails = (state, debt, portfolioCurrency) => {
  const portfolioTicker = getTickerUsingShortName(state, portfolioCurrency);
  if (debt.quantity !== undefined && debt.tickerId) {
    return {
      value: debt.quantity,
      currency: getTickerUsingId(state, debt.tickerId).shortName
    };
  } else if (debt.quantity !== undefined && !debt.tickerId && debt.price !== undefined && debt.priceTickerId) {
    return {
      value: debt.quantity * debt.price,
      currency: getTickerUsingId(state, debt.priceTickerId).shortName
    };
  } else if (debt.quantity !== undefined && !debt.tickerId && !debt.price && !debt.value) {
    return {
      value: debt.quantity,
      currency: portfolioTicker.shortName
    };
  } else if (debt.value !== undefined && debt.valueTickerId) {
    return {
      value: debt.value,
      currency: getTickerUsingId(state, debt.valueTickerId).shortName
    };
  }
};

export const isAssetSameAsCustodian = (state, custodian, { value, currency, rate, cost, costCurrency }) => {
  if (!custodian === true) {
    return false;
  }
  if (cost !== undefined && cost !== null) {
    if (cost !== custodian.cost) {
      return false;
    }
    if (
      costCurrency &&
      custodian.costTickerId &&
      costCurrency !== getTickerUsingId(state, custodian.costTickerId).shortName
    ) {
      return false;
    }
  }
  if ((value || custodian.value) && value !== custodian.value) {
    return false;
  }
  if (
    (currency || custodian.valueTickerId) &&
    currency !== getTickerUsingId(state, custodian.valueTickerId).shortName
  ) {
    return false;
  }
  if ((rate || custodian.rate) && custodian.valueTickerId === PVST_VALUE_TICKER_ID && rate !== custodian.rate) {
    return false;
  }
  return true;
};

export const isDebtSameAsCustodian = (state, custodian, { value, currency }) => {
  if (!custodian === true) {
    return false;
  }
  if ((value || custodian.value) && value !== custodian.value) {
    return false;
  }
  const custodianCurrency = custodian.valueTickerId
    ? getTickerUsingId(state, custodian.valueTickerId).shortName
    : undefined;
  if ((currency || custodianCurrency) && currency !== custodianCurrency) {
    return false;
  }
  return true;
};

export const getAccountEntryItemState = ({ state, location, isIgnored, isArchive, entryDetails, isAsset }) => {
  if (isIgnored) {
    return itemState.IGNORED;
  }
  if (isArchive) {
    return itemState.ARCHIVE;
  }
  if (location?.isCustodian === true) {
    const custodian = custodianSelector(state, location.id);
    const isSame = isAsset
      ? isAssetSameAsCustodian(state, custodian, {
          value: entryDetails?.value,
          currency: entryDetails?.currency,
          rate: entryDetails?.rate,
          cost: entryDetails?.cost,
          costCurrency: entryDetails?.costCurrency
        })
      : isDebtSameAsCustodian(state, custodian, {
          value: entryDetails?.value,
          currency: entryDetails?.currency
        });
    if (isSame) {
      return itemState.SAME;
    }
    return itemState.UPDATE;
  }
  return itemState.NEW;
};

export const getAccountEntryPreviousValue = ({ state, custodian, isQuantityBased }) => {
  if (!custodian) {
    return { previousValue: undefined, previousCurrency: undefined };
  }
  if (isQuantityBased) {
    return { previousValue: custodian.value, previousCurrency: undefined };
  }
  return {
    previousValue: custodian.value,
    previousCurrency: custodian.valueTickerId ? getTickerUsingId(state, custodian.valueTickerId).shortName : undefined
  };
};

const buildCustodianProperties = (state, item) => {
  const { details, isAsset } = item;
  const properties = {
    value: details.value,
    valueTickerId: details.valueTickerId || getTickerUsingShortName(state, details.currency).id,
    past: 0
  };
  if (details.valueExchangeRate) {
    properties.valueExchangeRate = details.valueExchangeRate;
  }
  if (details.rate) {
    properties.rate = details.rate;
  }
  if (isAsset && details.cost !== undefined) {
    properties.cost = details.cost;
    properties.costTickerId = details.costTickerId || getTickerUsingShortName(state, details.costCurrency).id;
    if (details.costExchangeRate) {
      properties.costExchangeRate = details.costExchangeRate;
    }
    properties.costType = costType.MANUAL;
  }
  return properties;
};

export const buildNewParentSyncItems = (state, dataExtracted, locationMap, portfolio) => {
  const items = [];
  const buildOne = (item, isAsset) => {
    const loc = locationMap[item.id];
    if (!loc || !loc.newParentCustodianId) return null;
    const rawDetails = isAsset
      ? getAssetDetails(state, item, portfolio.currency)
      : getDebtDetails(state, item, portfolio.currency);
    if (!rawDetails) return null;
    return {
      id: item.id,
      itemState: itemState.NEW,
      isAsset,
      name: item.name,
      extractedData: item,
      details: rawDetails
    };
  };
  for (const a of dataExtracted.asset || []) {
    const built = buildOne(a, true);
    if (built) items.push(built);
  }
  for (const d of dataExtracted.debt || []) {
    const built = buildOne(d, false);
    if (built) items.push(built);
  }
  return items;
};

export const createUsageStatusCollector = () => ({
  ignoredHashes: [],
  usedHashes: [],
  stat: {}
});

export const addToUsageStatusCollector = (collector, ignoredHashes, usedHashes, stat) => {
  collector.ignoredHashes.push(...ignoredHashes);
  collector.usedHashes.push(...usedHashes);
  for (const key in stat) {
    const existing = collector.stat[key] || { added: 0, updated: 0, ignored: 0 };
    collector.stat[key] = {
      added: existing.added + (stat[key].added || 0),
      updated: existing.updated + (stat[key].updated || 0),
      ignored: existing.ignored + (stat[key].ignored || 0)
    };
  }
};

export const flushUsageStatusCollector = (dispatch, portfolioId, documentId, collector) => {
  dispatch(
    updateAiImportDocumentUsageStatus(
      portfolioId,
      documentId,
      collector.ignoredHashes,
      collector.usedHashes,
      collector.stat
    )
  )
    .then(() => {})
    .catch(() => {});
};

export const bulkEntityType = {
  CASH_FLOW: "cashFlow",
  FUND_SCHEDULE: "fundSchedule"
};

export const createBulkSyncCollector = () => ({
  custodian: { create: [], update: [], archiveHolding: [] },
  cashFlow: { create: [], update: [] },
  fundSchedule: { create: [], update: [] },
  custodiansToRefresh: []
});

export const flushBulkSync = ({
  dispatch,
  portfolio,
  documentId,
  bulkCollector,
  usageStatusCollector,
  onProgress,
  onComplete,
  onError
}) => {
  dispatch(
    bulkSyncCustodianEntities({
      portfolioId: portfolio.id,
      custodian: bulkCollector.custodian,
      cashFlow: bulkCollector.cashFlow,
      fundSchedule: bulkCollector.fundSchedule,
      custodiansToRefresh: bulkCollector.custodiansToRefresh,
      onProgress,
      onComplete: result => {
        if (usageStatusCollector) {
          flushUsageStatusCollector(dispatch, portfolio.id, documentId, usageStatusCollector);
        }
        onComplete?.(result);
      },
      onError
    })
  );
};

const markCustodianUpdated = (dispatch, state, portfolioId, bulkCollector, custodianId) => {
  if (!custodianId) return;
  const existing = bulkCollector.custodian.update.find(entry => entry.id === custodianId);
  if (existing) {
    if (existing.past === 0) return;
    existing.past = 0;
  } else {
    const custodian = custodianSelector(state, custodianId);
    if (!custodian || custodian.past === 0) return;
    bulkCollector.custodian.update.push({ id: custodianId, sectionId: custodian.sectionId, past: 0 });
  }
  dispatch(updateCustodianInBulkAction(portfolioId, [{ id: custodianId, past: 0 }], false, undefined, ["past"]));
};

const markCustodianAndParentUpdated = (dispatch, state, portfolioId, bulkCollector, custodianId) => {
  markCustodianUpdated(dispatch, state, portfolioId, bulkCollector, custodianId);
  const parentId = custodianSelector(state, custodianId)?.parentId;
  markCustodianUpdated(dispatch, state, portfolioId, bulkCollector, parentId);
};

export const syncAccountItemsToPortfolio = ({
  dispatch,
  state,
  portfolio,
  documentId,
  items,
  locationMap,
  lastLinkMetadata,
  usageStatusCollector,
  bulkCollector
}) => {
  const custodianIdsToUpdate = [];
  const enqueuedNewSectionIds = new Set();
  const promotedParentIds = new Set();
  const ignoredHashes = [];
  const usedHashes = [];
  let addedCount = 0;
  let updatedCount = 0;
  let ignoredCount = 0;
  let archivedCount = 0;

  const addCustodianToRefresh = custodian => {
    if (!custodian) return;
    if (bulkCollector.custodiansToRefresh.some(item => item.id === custodian.id)) return;
    bulkCollector.custodiansToRefresh.push(custodian);
  };
  const addParentToRefresh = parentId => {
    if (!parentId) return;
    addCustodianToRefresh(custodianSelector(state, parentId));
  };

  const orderedItems = [
    ...items.filter(i => locationMap[i.id]?.newParentCustodianId !== undefined),
    ...items.filter(i => locationMap[i.id]?.newParentCustodianId === undefined)
  ];

  for (const item of orderedItems) {
    const { extractedData } = item;
    if (extractedData.ignore === true && item.itemState !== itemState.IGNORED) {
      usedHashes.push(extractedData.hash);
    } else if (extractedData.ignore !== true && item.itemState === itemState.IGNORED) {
      ignoredHashes.push(extractedData.hash);
    }

    if (item.itemState === itemState.IGNORED) {
      ignoredCount++;
      continue;
    }
    if (item.itemState === itemState.ARCHIVE) {
      const location = locationMap[item.id];
      if (location?.isCustodian === true && location.id) {
        bulkCollector.custodian.archiveHolding.push(location.id);
        addParentToRefresh(custodianSelector(state, location.id)?.parentId);
        custodianIdsToUpdate.push(location.id);
        archivedCount++;
      }
      continue;
    }
    if (item.itemState === itemState.SAME) {
      const location = locationMap[item.id];
      if (location?.isCustodian === true) {
        const existingCustodian = custodianSelector(state, location.id);
        const mergedLastLink = buildCustodianLastLink(existingCustodian?.lastLink, lastLinkMetadata, item.name);
        if (mergedLastLink && mergedLastLink !== (existingCustodian?.lastLink || "")) {
          dispatch(updateCustodianInBulkAction(portfolio.id, [{ ...existingCustodian, lastLink: mergedLastLink }]));
          bulkCollector.custodian.update.push({
            id: location.id,
            sectionId: existingCustodian.sectionId,
            lastLink: mergedLastLink
          });
        }
        markCustodianAndParentUpdated(dispatch, state, portfolio.id, bulkCollector, location.id);
      }
      continue;
    }

    const propertiesToUpdate = buildCustodianProperties(state, item);
    const location = locationMap[item.id];

    if (location.isCustodian === true) {
      const existingCustodian = custodianSelector(state, location.id);
      const mergedLastLink = buildCustodianLastLink(existingCustodian?.lastLink, lastLinkMetadata, item.name);
      if (mergedLastLink && mergedLastLink !== (existingCustodian?.lastLink || "")) {
        propertiesToUpdate.lastLink = mergedLastLink;
      }
      if (existingCustodian && existingCustodian.holdingsCount > 0) {
        addCustodianToRefresh(existingCustodian);
      }
      addParentToRefresh(existingCustodian?.parentId);
      dispatch(updateCustodianInBulkAction(portfolio.id, [{ ...existingCustodian, ...propertiesToUpdate }]));
      bulkCollector.custodian.update.push({
        id: location.id,
        sectionId: existingCustodian.sectionId,
        ...propertiesToUpdate
      });
      markCustodianAndParentUpdated(dispatch, state, portfolio.id, bulkCollector, location.id);
      custodianIdsToUpdate.push(location.id);
      updatedCount++;
      continue;
    }

    const section = location.isSection
      ? location
      : location.section || sheetSectionsSelector(state, portfolio.id, location.id)[0];
    const newCustodianId = location.newParentCustodianId || location.newCustodianId || getUuid();
    // The custodian may already have been created in this session (e.g. a combined
    // Assets + Cash flow sync triggered from another tab created it). Don't create it twice.
    if (location.newCustodianId && custodianSelector(state, newCustodianId)) {
      custodianIdsToUpdate.push(newCustodianId);
      continue;
    }
    const isChildCustodian = !!item.extractedData.parentAccount;
    const institution = lastLinkMetadata?.institution;
    const capitalizedInstitution = institution ? capitalizeStringWithSpaces(institution) : "";
    const newCustodianName =
      !isChildCustodian &&
      item.extractedData.isNamedByUser !== true &&
      capitalizedInstitution &&
      !item.name.toLowerCase().includes(capitalizedInstitution.toLowerCase())
        ? `${capitalizedInstitution} - ${item.name}`
        : item.name;
    const freshLastLink = buildCustodianLastLink("", lastLinkMetadata, item.name);
    if (freshLastLink) propertiesToUpdate.lastLink = freshLastLink;
    const newCustodian = {
      id: newCustodianId,
      name: newCustodianName,
      sectionId: section.id,
      ...propertiesToUpdate,
      origin: buildCustodianOrigin(lastLinkMetadata, userSelector(state)?.id)
    };
    if (location.newParentCustodianId !== undefined) {
      newCustodian.irrType = irrTypes.HOLDING;
      newCustodian.linkType = accountLinkingService.MANUAL_CONNECTED;
      newCustodian.linkAccountContainer = accountLinkingContainers.INVESTMENT;
      newCustodian.linkProviderId = getAccountLinkingService(accountLinkingService.MANUAL_CONNECTED);
      newCustodian.linkProviderName = getAggregatorName(accountLinkingService.MANUAL_CONNECTED);
      newCustodian.linkProviderAccountId = newCustodianId;
      newCustodian.linkAccountId = newCustodianId;
      newCustodian.linkAccountName = item.name;
      newCustodian.linkAccountMask = "";
      addCustodianToRefresh(newCustodian);
    }
    if (location.isParent === true && location.parentCustodianId) {
      newCustodian.parentId = location.parentCustodianId;
      newCustodian.linkType = undefined;
      newCustodian.linkAccountContainer = undefined;
      newCustodian.linkProviderId = undefined;
      newCustodian.linkProviderName = undefined;
      newCustodian.linkProviderAccountId = undefined;
      newCustodian.linkAccountId = undefined;
      newCustodian.linkAccountName = undefined;
      newCustodian.linkAccountMask = undefined;
      if (location.isNewParent === true) {
        newCustodian.hidden = 1;
      } else {
        const parentCustodian = custodianSelector(state, location.parentCustodianId);
        newCustodian.hidden = parentCustodian?.hidden === 1 ? 0 : 1;
        addParentToRefresh(location.parentCustodianId);
        if (parentCustodian && !parentCustodian.linkType && !promotedParentIds.has(parentCustodian.id)) {
          promotedParentIds.add(parentCustodian.id);
          const parentManualProperties = {
            irrType: irrTypes.HOLDING,
            linkType: accountLinkingService.MANUAL_CONNECTED,
            linkAccountContainer: accountLinkingContainers.INVESTMENT,
            linkProviderId: getAccountLinkingService(accountLinkingService.MANUAL_CONNECTED),
            linkProviderName: getAggregatorName(accountLinkingService.MANUAL_CONNECTED),
            linkProviderAccountId: parentCustodian.id,
            linkAccountId: parentCustodian.id,
            linkAccountName: parentCustodian.name,
            linkAccountMask: ""
          };
          dispatch(updateCustodianInBulkAction(portfolio.id, [{ ...parentCustodian, ...parentManualProperties }]));
          bulkCollector.custodian.update.push({
            id: parentCustodian.id,
            sectionId: parentCustodian.sectionId,
            ...parentManualProperties
          });
        }
        markCustodianUpdated(dispatch, state, portfolio.id, bulkCollector, location.parentCustodianId);
      }
    }

    const newCustodianSortKey = dispatch(
      insertCustodianAtEndOfSection(portfolio.id, section.id, newCustodian.id, undefined, newCustodian, false, true)
    );
    if (newCustodianSortKey) {
      newCustodian.sortKey = newCustodianSortKey;
    }

    if (location.createNewSection === true && enqueuedNewSectionIds.has(section.id) === false) {
      enqueuedNewSectionIds.add(section.id);
      if (location.reuseEmptySection === true) {
        dispatch(updateSection(portfolio.id, section.sheetId, section.id, { name: section.name }));
      } else {
        dispatch(
          insertSection(portfolio.id, section.sheetId, {
            id: location.id,
            name: location.name,
            sortKey: location.sortKey,
            sheetId: section.sheetId
          })
        );
      }
    }

    bulkCollector.custodian.create.push(newCustodian);
    custodianIdsToUpdate.push(newCustodian.id);
    addedCount++;
  }

  dispatch(updateDashboardAction(custodianIdsToUpdate));
  const accountStat = {
    account: { added: addedCount, updated: updatedCount + archivedCount, ignored: ignoredCount }
  };
  if (usageStatusCollector) {
    addToUsageStatusCollector(usageStatusCollector, ignoredHashes, usedHashes, accountStat);
  } else {
    dispatch(updateAiImportDocumentUsageStatus(portfolio.id, documentId, ignoredHashes, usedHashes, accountStat))
      .then(() => {})
      .catch(() => {});
  }

  return { custodianIdsToUpdate, addedCount, updatedCount, ignoredCount, archivedCount };
};

export const getCashflowItemDetails = (state, cashflow) => {
  if (cashflow.quantity !== undefined && cashflow.tickerId) {
    return {
      value: cashflow.quantity,
      valueTickerId: cashflow.tickerId,
      valueExchangeRate: cashflow.exchangeRate,
      currency: getTickerUsingId(state, cashflow.tickerId).shortName
    };
  } else if (cashflow.value !== undefined && cashflow.valueTickerId) {
    return {
      value: cashflow.value,
      valueTickerId: cashflow.valueTickerId,
      valueExchangeRate: cashflow.valueExchangeRate,
      currency: getTickerUsingId(state, cashflow.valueTickerId).shortName
    };
  }
};

export const buildCashflowLocationMap = (state, custodianNameCashflowMap, options = {}) => {
  const locationMap = {};
  for (const name in custodianNameCashflowMap) {
    const cashflows = custodianNameCashflowMap[name];
    cashflows.sort((a, b) => a.date.localeCompare(b.date));

    const first = cashflows[0];
    if (!first.matchedCustodianId && first.accountId && options.extractedAccounts) {
      const account = findExtractedAccountById(options.extractedAccounts, first.accountId);
      const accountLocation = account ? options.accountLocationMap?.[account.id] : null;
      if (accountLocation?.isCustodian === true) {
        const matchedCustodian = custodianSelector(state, accountLocation.id);
        if (matchedCustodian && (!options.custodianFilter || options.custodianFilter(state, matchedCustodian))) {
          locationMap[name] = accountLocation;
        }
        continue;
      }
      const newCustodianId = account ? options.newCustodianIdByAccountId?.[account.id] : undefined;
      if (account && accountLocation && newCustodianId) {
        // Account already created this session: its newCustodianId now resolves to a real custodian,
        // so attach to it rather than creating it again.
        const createdCustodian = custodianSelector(state, newCustodianId);
        if (createdCustodian) {
          const section = sectionSelector(state, createdCustodian.sectionId);
          const sheet = section ? sheetSelector(state, section.sheetId) : null;
          if (section && sheet && (!options.custodianFilter || options.custodianFilter(state, createdCustodian))) {
            locationMap[name] = { isCustodian: true, section, sheet, ...createdCustodian };
          }
          continue;
        }

        locationMap[name] = {
          isNewAccount: true,
          newCustodianId,
          accountId: account.id,
          name: account.name,
          section: accountLocation.section,
          sheet: accountLocation.sheet,
          createNewSection: accountLocation.createNewSection === true,
          reuseEmptySection: accountLocation.reuseEmptySection === true,
          isParent: accountLocation.isParent,
          isNewParent: accountLocation.isNewParent,
          parentCustodianId: accountLocation.parentCustodianId,
          parentCustodianName: accountLocation.parentCustodianName
        };
      }
      continue;
    }

    const custodian = custodianSelector(state, first.matchedCustodianId);
    if (!custodian) {
      continue;
    }
    if (options.custodianFilter && !options.custodianFilter(state, custodian)) {
      continue;
    }
    const section = sectionSelector(state, custodian.sectionId);
    const sheet = section ? sheetSelector(state, section.sheetId) : null;
    if (!section || !sheet) {
      continue;
    }
    locationMap[name] = {
      isCustodian: true,
      section,
      sheet,
      ...custodian
    };
  }
  return { locationMap };
};

export const setMatchedCashflowIds = (items, locationMap, idMap, matchPredicate) => {
  for (const item of items) {
    const custodian = locationMap[item.name];
    if (custodian && idMap[custodian.id]) {
      const matching = idMap[custodian.id].find(existing => matchPredicate(existing, item));
      item.matchedCashflowId = matching ? matching.id : undefined;
    }
  }
};

export const syncCashflowsToPortfolio = ({
  dispatch,
  state,
  portfolio,
  documentId,
  custodianCashflowMap,
  entityType,
  ignoredHashes,
  usedHashes,
  addedCount,
  updatedCount,
  ignoredCount,
  lastLinkMetadata,
  skipLastLinkForCustodianIds,
  usageStatusCollector,
  bulkCollector
}) => {
  const cashflowStat = { cashFlow: { added: addedCount, updated: updatedCount, ignored: ignoredCount } };
  if (usageStatusCollector) {
    addToUsageStatusCollector(usageStatusCollector, ignoredHashes, usedHashes, cashflowStat);
  } else {
    dispatch(updateAiImportDocumentUsageStatus(portfolio.id, documentId, ignoredHashes, usedHashes, cashflowStat))
      .then(() => {})
      .catch(() => {});
  }

  const group = entityType === bulkEntityType.FUND_SCHEDULE ? bulkCollector.fundSchedule : bulkCollector.cashFlow;

  for (const [custodianId, cashflows] of Object.entries(custodianCashflowMap)) {
    for (const cashflow of cashflows) {
      const entry = { ...cashflow, custodianId };
      if (cashflow.isUpdate) {
        group.update.push(entry);
      } else {
        group.create.push(entry);
      }
    }

    if (cashflows.length > 0 && !skipLastLinkForCustodianIds?.has(custodianId)) {
      const custodian = custodianSelector(state, custodianId);
      let mergedLastLink = custodian?.lastLink || "";
      for (const cashflow of cashflows) {
        for (const name of getLastLinkNames(cashflow.lastLink)) {
          mergedLastLink = buildCustodianLastLink(mergedLastLink, lastLinkMetadata, name);
        }
      }
      if (mergedLastLink && mergedLastLink !== (custodian?.lastLink || "")) {
        dispatch(updateCustodianInBulkAction(portfolio.id, [{ ...custodian, lastLink: mergedLastLink }]));
        bulkCollector.custodian.update.push({
          id: custodianId,
          sectionId: custodian?.sectionId,
          lastLink: mergedLastLink
        });
      }
    }

    if (cashflows.length > 0) {
      markCustodianAndParentUpdated(dispatch, state, portfolio.id, bulkCollector, custodianId);
    }
  }
};

// Cashflows attached to an account created in the same batch can't merge their name into the
// custodian via a custodian.update (the custodian doesn't exist yet and the update would clobber
// the create's lastLink). Instead fold each cashflow's name(s) into the new custodian's create
// lastLink so the account carries both its own name and the cashflow names.
export const mergeNewCustodianCashflowLastLinks = ({ bulkCollector, lastLinkMetadata, newCustodianIds }) => {
  if (!newCustodianIds || newCustodianIds.size === 0) {
    return;
  }
  const cashflowEntries = [...(bulkCollector.cashFlow.create || []), ...(bulkCollector.fundSchedule.create || [])];
  for (const custodian of bulkCollector.custodian.create) {
    if (!newCustodianIds.has(custodian.id)) {
      continue;
    }
    let mergedLastLink = custodian.lastLink || "";
    for (const entry of cashflowEntries) {
      if (entry.custodianId !== custodian.id) {
        continue;
      }
      for (const name of getLastLinkNames(entry.lastLink)) {
        mergedLastLink = buildCustodianLastLink(mergedLastLink, lastLinkMetadata, name);
      }
    }
    if (mergedLastLink) {
      custodian.lastLink = mergedLastLink;
    }
  }
};
